import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../config/api';
import { api, handleUnauthorized } from '../../services/api';
import { useParams } from "react-router-dom";
import DocumentViewerHeader from "@/components/document-viewer/document-viewer-header";
import DocumentApprovalSidebar from "@/components/document-viewer/document-approval-sidebar";
import DocumentCanvas from "@/components/document-editor/document-canvas";
import MobileApprovalButtons from "@/components/document-viewer/mobile-approval-buttons";
import ApprovalActionModal from "@/components/document-viewer/approval-action-modal";
import ShareDocumentModal from "@/components/document-viewer/share-document-modal";
import FieldInputModal from "@/components/document-viewer/field-input-modal";
import type { SignableFieldData } from "@/components/document-viewer/signable-field";
import OtpVerificationModal from "@/components/otp-verification-modal";
import { toast } from "@/components/ui/use-toast";
import { Loader2 } from "lucide-react";
import { useAuthContext } from '../../contexts/AuthContext';

const FIELD_TYPE_KEYS: Record<string, string> = {
  signature: 'viewer_field_signature',
  initial: 'viewer_field_initial',
  date: 'viewer_field_date',
  name: 'viewer_field_name',
  email: 'viewer_field_email',
  city: 'viewer_field_city',
  position: 'viewer_field_position',
  province: 'viewer_field_province',
  state: 'viewer_field_state',
};

export default function DocumentViewerPage() {
  const { documentId = "default-document" } = useParams();
  const { user: currentUser } = useAuthContext();
  const { t } = useTranslation(['documents', 'common']);
  const [documentName, setDocumentName] = useState("Contract Agreement.pdf");
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Define interfaces for API response types
  interface Document {
    id: number;
    title: string;
    status: string;
    signing_certificate_path?: string | null;
    signing_requests?: SigningRequest[];
    requestor?: {
      id: number;
      full_name?: string;
      name?: string;
      first_name?: string;
      last_name?: string;
      email: string;
    };
    due_date?: string;
  }

  interface SigningRequest {
    id: number;
    document_id: number;
    signatory?: {
      id: number;
      name?: string;
      first_name?: string;
      last_name?: string;
      email: string;
    };
    status: string;
    created_at?: string;
    signed_at?: string;
    declined_at?: string;
    remarks?: string;
  }

  interface DocumentField {
    id: number;
    field_type: string;
    x_position: number;
    y_position: number;
    page_number: number;
    width: number;
    height: number;
    recipient_id?: number;
    recipient?: {
      id: number;
      name?: string;
      first_name?: string;
      last_name?: string;
      email: string;
    };
    signature_image_url?: string;
  }

  interface ApiResponse<T> {
    document?: Document;
    download_url?: string;
    fields?: DocumentField[];
    data?: T[];
  }

  const [documentData, setDocumentData] = useState<Document | null>(null);
  const [documentFields, setDocumentFields] = useState<any[]>([]);
  const [signingRequestId, setSigningRequestId] = useState<string | null>(null);
  const [isDocumentSigned, setIsDocumentSigned] = useState(false);
  const [signingInfo, setSigningInfo] = useState<any[]>([]);
  const [currentUserRequestStatus, setCurrentUserRequestStatus] = useState<string | null>(null);
  const [currentUserDeclineReason, setCurrentUserDeclineReason] = useState<string | null>(null);
  const [currentUserDeclinedAt, setCurrentUserDeclinedAt] = useState<string | null>(null);
  const [savedSignatureImage, setSavedSignatureImage] = useState<string | null>(null);

  // Per-field signing state
  const [signableFields, setSignableFields] = useState<SignableFieldData[]>([]);
  const [fieldModalOpen, setFieldModalOpen] = useState(false);
  const [activeField, setActiveField] = useState<SignableFieldData | null>(null);
  const [capturedSignatureImage, setCapturedSignatureImage] = useState<string | null>(null);

  // Whether the current user has a pending signing request (enables signing mode)
  const isSigningMode = currentUserRequestStatus === 'pending' && !isDocumentSigned;

  // Fetch the authenticated user's saved signature if they have one
  useEffect(() => {
    if (!currentUser?.signature_image_path || !currentUser?.id) return;
    const fetchSignature = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/users/${currentUser.id}/signature`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        });
        if (!res.ok) return;
        const blob = await res.blob();
        const reader = new FileReader();
        reader.onload = () => setSavedSignatureImage(reader.result as string);
        reader.readAsDataURL(blob);
      } catch { /* ignore */ }
    };
    fetchSignature();
  }, [currentUser?.id, currentUser?.signature_image_path]);

  // Extracted fetch function so it can be called after signing
  const fetchDocumentData = async () => {
      if (!documentId || documentId === "default-document") return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch document data using API service instead of hardcoded URL
        const documentResult = await api.get<ApiResponse<any>>(`/documents/${documentId}`) as any;
        
        if (!documentResult) {
          throw new Error(`Failed to fetch document`);
        }
        
        console.log("Document data:", documentResult);
        
        // API returns { document: {...} } directly
        const doc = documentResult.document || documentResult.data?.document;
        if (doc) {
          setDocumentData(doc as Document);
          setDocumentName(doc.title || t('documents:viewer_unnamed_document'));
          
          // Check if document is signed/completed/declined based on status
          const documentStatus = doc.status;
          if (documentStatus === 'signed' || documentStatus === 'completed' || documentStatus === 'partially_signed' || documentStatus === 'declined') {
            setIsDocumentSigned(true);
            
            // Extract signing information if available
            if (doc.signing_requests && doc.signing_requests.length > 0) {
              const signingDetails = doc.signing_requests.map((request: any) => ({
                id: request.id,
                signatory: request.signatory ? {
                  name: request.signatory.name || `${request.signatory.first_name || ''} ${request.signatory.last_name || ''}`.trim(),
                  email: request.signatory.email
                } : null,
                status: request.status,
                signedAt: request.signed_at ? new Date(request.signed_at).toLocaleString() : null,
                declinedAt: request.declined_at ? new Date(request.declined_at).toLocaleString() : null,
                declineReason: request.decline_reason || null,
                remarks: request.remarks || null
              }));
              
              setSigningInfo(signingDetails);
              console.log("Signing information:", signingDetails);
            }
          }

          // Fetch signing request ID for the current user and document
          try {
            // Using API service instead of hardcoded URL for signing requests
            const signingRequestsData = await api.get<ApiResponse<SigningRequest>>('/signing-requests', {
              params: {
                document_id: documentId,
                current_user_only: true,
              }
            }) as any;
            
            if (signingRequestsData) {
              console.log("Signing requests data:", signingRequestsData);
              
              // Laravel pagination returns data in a 'data' property
              const requests = signingRequestsData.data || [];
              const matchingRequest = requests.find((request: any) => 
                request.document_id.toString() === documentId
              );
              
              if (matchingRequest) {
                console.log("Found matching signing request:", matchingRequest);
                setSigningRequestId(matchingRequest.id.toString());
                setCurrentUserRequestStatus(matchingRequest.status);
                if (matchingRequest.decline_reason) {
                  setCurrentUserDeclineReason(matchingRequest.decline_reason);
                }
                if (matchingRequest.declined_at) {
                  setCurrentUserDeclinedAt(matchingRequest.declined_at);
                }
              } else {
                console.warn("No signing request found for this document and user");
              }
            }
          } catch (signingRequestError) {
            console.error("Error fetching signing requests:", signingRequestError);
            // Don't throw error for signing request, just log it
          }

          // Fetch document fields
          try {
            const fieldsData = await api.get<ApiResponse<DocumentField>>(`/documents/${documentId}/fields`) as any;
            
            if (fieldsData) {
              const fields = (fieldsData.fields || fieldsData.data?.fields || []) as DocumentField[];
              
              if (fields && Array.isArray(fields)) {
                // Find recipient IDs belonging to the current user
                const myRecipientIds = (doc.recipients || [])
                  .filter((r: any) => r.user_id === currentUser?.id)
                  .map((r: any) => r.id?.toString());

                // Transform to read-only PlacedField format (for non-signing mode)
                const transformedFields = fields.map((field) => {
                  const baseField = {
                    id: field.id.toString(),
                    type: field.field_type,
                    label: FIELD_TYPE_KEYS[field.field_type] ? t(`documents:${FIELD_TYPE_KEYS[field.field_type]}`) : field.field_type.charAt(0).toUpperCase() + field.field_type.slice(1),
                    x: field.x_position,
                    y: field.y_position,
                    pageNumber: field.page_number,
                    width: field.width,
                    height: field.height,
                    recipientId: field.recipient_id?.toString(),
                    readOnly: true,
                  };
                  
                  const signedData = (field as any).signed_data;
                  if (field.field_type === 'signature' && signedData) {
                    return {
                      ...baseField,
                      fullName: signedData.signatory_name || null,
                      signatureImage: signedData.signature_image_url || null
                    };
                  }
                  
                  return baseField;
                });
                setDocumentFields(transformedFields);

                // Transform to SignableFieldData format (for signing mode)
                const signable: SignableFieldData[] = fields.map((field) => {
                  const isSigner = myRecipientIds.includes(field.recipient_id?.toString());
                  const signedData = (field as any).signed_data;
                  const sf: SignableFieldData = {
                    id: field.id.toString(),
                    type: field.field_type,
                    label: field.field_type.charAt(0).toUpperCase() + field.field_type.slice(1),
                    x: field.x_position,
                    y: field.y_position,
                    pageNumber: field.page_number,
                    width: field.width,
                    height: field.height,
                    recipientId: field.recipient_id?.toString(),
                    isSignerField: isSigner,
                    value: null,
                  };
                  if (signedData) {
                    sf.value = signedData.value || signedData.signatory_name || 'Signed';
                    sf.signatureImage = signedData.signature_image_url || null;
                    sf.fullName = signedData.signatory_name || null;
                    sf.isSignerField = false;
                  }
                  return sf;
                });
                setSignableFields(signable);
              }
            }
          } catch (fieldsError) {
            console.error("Error fetching document fields:", fieldsError);
          }
          
          // Fetch the download URL for the document using the API service
          const downloadData = await api.get<ApiResponse<any>>(`/documents/${documentId}/download`) as any;
          
          if (downloadData && downloadData.download_url) {
            // Append cache-busting param to ensure browser fetches the latest version
            const separator = downloadData.download_url.includes('?') ? '&' : '?';
            setDocumentUrl(`${downloadData.download_url}${separator}t=${Date.now()}`);
          } else {
            throw new Error("No download URL provided");
          }
        } else {
          throw new Error("Invalid document data structure - no document found in response");
        }
      } catch (err: any) {
        console.error("Error fetching document:", err);
        setError(err.message || t('documents:viewer_toast_load_failed'));
        toast({
          title: t('documents:viewer_toast_error'),
          description: err.message || t('documents:viewer_toast_load_failed'),
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
  };

  // Fetch document data on mount / documentId change
  useEffect(() => {
    fetchDocumentData();
  }, [documentId]);

  // State for share modal
  const [shareModalOpen, setShareModalOpen] = useState(false);

  // State for OTP verification
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [pendingApprovalRemarks, setPendingApprovalRemarks] = useState("");

  // State for mobile approval modal
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [approvalAction, setApprovalAction] = useState<
    "approve" | "decline" | null
  >(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDownloadCertificate = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/documents/${documentId}/certificate`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (!response.ok) throw new Error('Certificate download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificate-${documentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({ title: t('documents:certificate_title'), description: t('documents:certificate_download') });
    } catch (error: any) {
      toast({ title: t('documents:viewer_toast_error'), description: error.message || t('documents:certificate_not_available'), variant: 'destructive' });
    }
  };

  const handleDownload = async () => {
    if (!documentUrl) {
      toast({ title: t('documents:viewer_toast_error'), description: t('documents:viewer_toast_download_no_doc'), variant: "destructive" });
      return;
    }
    try {
      const response = await fetch(documentUrl, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = documentName || 'document.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({ title: t('documents:viewer_toast_download_started'), description: t('documents:viewer_toast_download_started_desc') });
    } catch (error: any) {
      console.error('Download error:', error);
      toast({ title: t('documents:viewer_toast_download_failed'), description: error.message || t('documents:viewer_toast_download_failed_desc'), variant: "destructive" });
    }
  };

  const handlePrint = () => {
    toast({
      title: t('documents:viewer_toast_print_title'),
      description: t('documents:viewer_toast_print_desc'),
    });
    // In a real application, you would use window.print() or a printing library
  };

  const handleShare = () => {
    setShareModalOpen(true);
  };

  // Per-field signing: compute progress from signableFields
  const signerFields = useMemo(() => signableFields.filter(f => f.isSignerField), [signableFields]);
  const completedSignerFields = useMemo(() => signerFields.filter(f => f.value || f.signatureImage), [signerFields]);
  const allFieldsCompleted = signerFields.length > 0 && completedSignerFields.length === signerFields.length;

  // Handle field click in signing mode
  const handleFieldClick = useCallback((field: SignableFieldData) => {
    setActiveField(field);
    setFieldModalOpen(true);
  }, []);

  // Handle field value confirmation from FieldInputModal
  const handleFieldConfirm = useCallback((fieldId: string, value: string, signatureImage?: string | null) => {
    setSignableFields(prev => prev.map(f => {
      if (f.id !== fieldId) return f;
      return {
        ...f,
        value,
        signatureImage: signatureImage || undefined,
        fullName: (f.type === 'signature' || f.type === 'initial') ? value : undefined,
      };
    }));
    if (signatureImage) {
      setCapturedSignatureImage(signatureImage);
    }
  }, []);

  const handleApprove = async (remarks: string) => {
    // Store remarks and open OTP modal for identity verification
    setPendingApprovalRemarks(remarks);
    setApprovalModalOpen(false);
    setOtpModalOpen(true);
  };

  const handleOtpVerified = async (otpToken: string) => {
    setIsSubmitting(true);

    try {
      if (!signingRequestId) {
        throw new Error('No signing request found for this document');
      }

      // Send per-field values from the signing session
      const signedFields = signerFields.map(f => ({
        document_field_id: f.id,
        value: f.value || f.fullName || null,
      }));

      const response = await fetch(`${API_BASE_URL}/signing-requests/${signingRequestId}/sign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          signed_fields: signedFields,
          remarks: pendingApprovalRemarks || '',
          otp_token: otpToken,
          consent: true,
          signature_image: capturedSignatureImage || undefined,
        })
      });

      if (!response.ok) {
        if (response.status === 401) { handleUnauthorized(); return; }
        const error = await response.json();
        throw new Error(error.message || 'Failed to approve document');
      }

      const data = await response.json();

      setIsDocumentSigned(true);

      toast({
        title: t('documents:viewer_toast_approved'),
        description: data.completed_document_path
          ? t('documents:viewer_toast_approved_completed')
          : pendingApprovalRemarks
            ? t('documents:viewer_toast_approved_with_remarks')
            : t('documents:viewer_toast_approved_success'),
        variant: "success",
      });

      window.dispatchEvent(new Event('signing-count-changed'));

      await fetchDocumentData();

    } catch (error: any) {
      console.error('Error approving document:', error);
      toast({
        title: t('documents:viewer_toast_approval_failed'),
        description: error.message || t('documents:viewer_toast_approval_failed_desc'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
      setPendingApprovalRemarks("");
    }
  };

  const handleDecline = async (reason: string, withdrawConsent?: boolean) => {
    setIsSubmitting(true);

    try {
      // Make sure we have a signing request ID
      if (!signingRequestId) {
        throw new Error('No signing request found for this document');
      }

      // Special case for the decline endpoint - we use direct fetch to handle the specific format
      // This could be refactored to use the api service in the future if needed
      const response = await fetch(`${API_BASE_URL}/signing-requests/${signingRequestId}/decline`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          decline_reason: reason || 'No reason provided',
          withdraw_consent: withdrawConsent || false,
        })
      });

      if (!response.ok) {
        if (response.status === 401) { handleUnauthorized(); return; }
        const error = await response.json();
        throw new Error(error.message || 'Failed to decline document');
      }

      // We've received a successful response
      await response.json(); // Consume the response but no need to store it
      
      toast({
        title: t('documents:viewer_toast_declined'),
        description: t('documents:viewer_toast_declined_desc', { reason }),
        variant: "destructive",
      });

      // Update the signing requests badge count in the sidebar
      window.dispatchEvent(new Event('signing-count-changed'));

      // Refresh document data to show updated declined status
      await fetchDocumentData();
      
    } catch (error: any) {
      console.error('Error declining document:', error);
      toast({
        title: t('documents:viewer_toast_decline_failed'),
        description: error.message || t('documents:viewer_toast_decline_failed_desc'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
      setApprovalModalOpen(false);
    }
  };

  const handleMobileDeclineClick = () => {
    setApprovalAction("decline");
    setApprovalModalOpen(true);
  };

  const handleModalSubmit = (text: string, withdrawConsent?: boolean) => {
    if (approvalAction === "decline") {
      handleDecline(text, withdrawConsent);
    }
  };

  return (
    <div className="flex flex-col h-screen">
        <DocumentViewerHeader
          documentName={documentName}
          onDownload={handleDownload}
          onPrint={handlePrint}
          onShare={handleShare}
          onDownloadCertificate={handleDownloadCertificate}
          showCertificate={documentData?.status === 'completed' && !!documentData?.signing_certificate_path}
        />

        <div className="flex flex-1 overflow-hidden">
          <div className="flex-1 overflow-auto p-6 bg-muted/20 flex flex-col">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-2">{t('documents:viewer_loading')}</span>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-destructive">{error}</p>
              </div>
            ) : documentUrl ? (
              <DocumentCanvas
                documentUrl={documentUrl}
                fields={isSigningMode ? [] : (isDocumentSigned ? [] : documentFields)}
                signingMode={isSigningMode}
                signableFields={isSigningMode ? signableFields : []}
                onFieldClick={isSigningMode ? handleFieldClick : undefined}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <p>{t('documents:viewer_no_document')}</p>
              </div>
            )}

            {/* Mobile Approval Buttons - only show decline for mobile, signing is per-field */}
            {!isLoading && !error && documentUrl && !isSigningMode && (
              <div className="mt-4 flex lg:hidden justify-center gap-4">
                <MobileApprovalButtons
                  onApproveClick={() => {}}
                  onDeclineClick={handleMobileDeclineClick}
                />
              </div>
            )}
          </div>

          {documentData && !isLoading && !error && (
            <DocumentApprovalSidebar
              documentName={documentName}
              documentId={documentId}
              requesterName={documentData.requestor?.full_name || documentData.requestor?.name || `${documentData.requestor?.first_name || ''} ${documentData.requestor?.last_name || ''}`.trim() || t('documents:viewer_unknown_requester')}
              requesterEmail={documentData.requestor?.email || "unknown@example.com"}
              requesterAvatar="/avatar-placeholder.png"
              dueDate={documentData.signing_requests?.[0]?.created_at ? new Date(documentData.signing_requests[0].created_at).toLocaleDateString() : "Not specified"}
              onApprove={handleApprove}
              onDecline={handleDecline}
              isDocumentSigned={isDocumentSigned}
              signingInfo={signingInfo}
              currentUserRequestStatus={currentUserRequestStatus}
              currentUserDeclineReason={currentUserDeclineReason}
              currentUserDeclinedAt={currentUserDeclinedAt}
              signingRequestId={signingRequestId}
              signerName={currentUser?.first_name ? `${currentUser.first_name} ${currentUser.last_name || ''}`.trim() : ''}
              savedSignatureImage={savedSignatureImage}
              fieldProgress={isSigningMode ? { completed: completedSignerFields.length, total: signerFields.length } : undefined}
              allFieldsCompleted={allFieldsCompleted}
            />
          )}
        </div>

        <ApprovalActionModal
          open={approvalModalOpen}
          onOpenChange={setApprovalModalOpen}
          action={approvalAction}
          onSubmit={handleModalSubmit}
          isSubmitting={isSubmitting}
          signerName={currentUser?.first_name ? `${currentUser.first_name} ${currentUser.last_name || ''}`.trim() : ''}
          savedSignatureImage={savedSignatureImage}
        />

        <FieldInputModal
          open={fieldModalOpen}
          onOpenChange={setFieldModalOpen}
          field={activeField}
          onConfirm={handleFieldConfirm}
          signerName={currentUser?.first_name ? `${currentUser.first_name} ${currentUser.last_name || ''}`.trim() : ''}
          signerEmail={currentUser?.email || ''}
          savedSignatureImage={savedSignatureImage}
        />

        <ShareDocumentModal
          open={shareModalOpen}
          onOpenChange={setShareModalOpen}
          documentId={documentId}
          documentName={documentName}
        />

        <OtpVerificationModal
          open={otpModalOpen}
          onOpenChange={setOtpModalOpen}
          onVerified={handleOtpVerified}
        />
      </div>
  );
}
